import type { ReactNode } from 'react';
import ConfirmDeleteActions from './ConfirmDeleteActions';

interface DeleteConfirmOverlayProps {
  /** Heading text shown at the top of the card. */
  title: string;
  /**
   * Optional icon node rendered in a red circle to the left of the title.
   * When provided, the title sits in a `flex items-center gap-3 mb-4` row
   * (icon-header layout). When omitted, the title renders as a plain
   * `<h3 className="... mb-2">` with no icon (matching the no-icon call sites).
   */
  icon?: ReactNode;
  /** Body message; accepts rich nodes (e.g. a highlighted entity name). */
  children: ReactNode;
  /** Cancel handler (passed verbatim from the call site). */
  onCancel: () => void;
  /** Confirm/delete handler (passed verbatim from the call site). */
  onConfirm: () => void;
  /** Disabled + loading flag for the confirm button. */
  loading: boolean;
  /** Label for the confirm button (defaults to ConfirmDeleteActions' 'Delete'). */
  confirmLabel?: string;
}

/**
 * Centered overlay + card wrapper for delete/remove confirmation modals.
 * Renders the dark overlay, the white card, a title (optionally with a red
 * icon header), a message body slot, and the shared ConfirmDeleteActions row.
 * Callers gate rendering themselves (this always renders when mounted).
 */
export default function DeleteConfirmOverlay({
  title,
  icon,
  children,
  onCancel,
  onConfirm,
  loading,
  confirmLabel,
}: DeleteConfirmOverlayProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        {icon ? (
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              {icon}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
        ) : (
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        )}
        <p className="text-sm text-gray-600 mb-6">{children}</p>
        <ConfirmDeleteActions
          onCancel={onCancel}
          onConfirm={onConfirm}
          loading={loading}
          confirmLabel={confirmLabel}
        />
      </div>
    </div>
  );
}
